// Settings → Privacy (privacy notice sections 6 and 11 to 13, M2-41). Everyone sees their consents, each with the
// notice version they answered, and withdraws them as easily as they gave them, after reading what it stops (#484);
// and makes a rights request, which
// LAMP confirms with a code to their registered email or mobile. A request about the business's data goes to the
// business within 72 hours; one about their own LAMP account goes to LAMP's Grievance Officer. An Owner also sees
// the requests the business must answer, who at LAMP looked at its data and why, and offboarding: the export, each
// confirmation in the words that will be logged, the optional archive, and the crypto-shred. The export, starting
// offboarding and the crypto-shred each need a step-up first (ADR-0019), which the step-up dialog asks for.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ApiError, type Api } from "../api.js";
import { formatDate } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { CATALOGUES, useLanguage, useWords, type Words } from "../i18n/words.js";
import { noticeHref } from "../legal/Notice.js";
import { useStepUp } from "../step-up/StepUp.js";
import {
  RIGHTS,
  type Consent,
  type ConsentPurpose,
  type ExportFile,
  type NewRightsRequest,
  type Offboarding,
  type OffboardingConfirmation,
  type PrivacyOverview,
  type RequestReceipt,
  type ReviewerAccessEntry,
  type Right,
  type RightsRequest,
} from "./api.js";

/**
 * A chat command that opens Settings → Privacy: "privacy", or "withdraw consent" (privacy notice section 12),
 * typed in English or Hindi ("गोपनीयता", "सहमति वापस लें").
 */
export type PrivacyCommand = "privacy" | "withdraw_consent";

export function privacyCommand(text: string): PrivacyCommand | undefined {
  const message = text.trim().toLowerCase();
  if (/^\/?(?:privacy|गोपनीयता)$/u.test(message)) return "privacy";
  if (/^\/?withdraw(?:\s+my)?\s+consent$/.test(message)) return "withdraw_consent";
  if (/^\/?(?:मेरी\s+)?सहमति\s+वापस(?:\s+(?:लें|लो|लेना|लीजिए))?$/u.test(message)) return "withdraw_consent";
  return undefined;
}

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError && error.problems.length > 0 ? error.problems.join("; ") : w.privacyUnreachable;

/** Runs an action that may need a step-up, asking for it first when the api does. */
type StepUpRun = <T>(work: () => Promise<T>, what?: string) => Promise<T>;

/** Hands the browser the export to save, as a link to the file would. */
function save(file: ExportFile) {
  const url = URL.createObjectURL(file.zip);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  link.click();
  // Some browsers read the file only after the click returns.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** The Owner's download of all the business's data: one step-up, then the zip. */
function ExportButton({ api, run }: { api: Api; run: StepUpRun }) {
  const w = useWords();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const download = async () => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      const file = await run(() => api.privacy.exportData());
      save(file);
      setNotice(w.privacyExported(file.name));
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" className="btn" disabled={busy} onClick={() => void download()}>
        {w.privacyDownloadAll}
      </button>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </>
  );
}

/**
 * What a refused request says: when to try again for too many requests (#482), which never says whether the
 * identifier is anyone's on LAMP; otherwise the problems the Application API gave.
 */
const requestProblemOf = (error: unknown, w: Words, language: Language) => {
  const retryAfter =
    error instanceof ApiError && error.code === "rate_limited"
      ? error.problems.find((p) => p.startsWith("retryAfter: "))?.slice("retryAfter: ".length)
      : undefined;
  if (retryAfter === undefined || Number.isNaN(Date.parse(retryAfter))) return problemOf(error, w);
  const time = formatDate(retryAfter, language, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
  return w.privacyTooManyRequests(time);
};

/** A date in India's time zone, in `language`: "14 Oct 2026". */
export const day = (iso: string, language: Language = "en") =>
  formatDate(iso, language, { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });

const status = (w: Words, value: RightsRequest["status"]): string =>
  ({
    awaiting_code: w.privacyStatusAwaitingCode,
    verified: w.privacyStatusVerified,
    forwarded: w.privacyStatusForwarded,
    completed: w.privacyStatusCompleted,
    refused: w.privacyStatusRefused,
  })[value];

const purpose = (w: Words, value: ConsentPurpose): string =>
  ({
    account: w.privacyPurposeAccount,
    sensitive_data: w.privacyPurposeSensitiveData,
    quality_monitoring: w.privacyPurposeQualityMonitoring,
    product_news: w.privacyPurposeProductNews,
  })[value];

const rightOf = (w: Words, value: Right): string =>
  ({
    access: w.privacyRightAccess,
    correction: w.privacyRightCorrection,
    erasure: w.privacyRightErasure,
    withdraw_consent: w.privacyRightWithdrawConsent,
    nominate: w.privacyRightNominate,
    grievance: w.privacyRightGrievance,
  })[value];

/** Where a confirmed request went, and when it will be answered, in `language`. */
export function requestNotice(request: RightsRequest, language: Language = "en"): string {
  const w = CATALOGUES[language];
  const on = (iso: string) => day(iso, language);
  if (request.about === "business") {
    return request.status === "forwarded" && request.forwardedAt
      ? w.privacyPassedToBusiness(request.reference, on(request.forwardedAt))
      : w.privacyConfirmedForBusiness(request.reference, on(request.forwardBy ?? request.dueBy));
  }
  return request.status === "forwarded"
    ? w.privacyWithGrievanceOfficer(request.reference, on(request.dueBy))
    : w.privacyConfirmedForLamp(request.reference, on(request.dueBy));
}

function Consents({
  consents,
  noticeVersion,
  busy,
  onChange,
}: {
  consents: readonly Consent[];
  noticeVersion: string;
  busy: boolean;
  onChange: (purpose: ConsentPurpose, give: boolean) => void;
}) {
  const w = useWords();
  const { language } = useLanguage();
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <caption className="small muted">
          <a href={noticeHref("privacy-notice", language)}>{w.privacyNoticeVersion(noticeVersion)}</a>
        </caption>
        <thead>
          <tr>
            <th scope="col">{w.privacyWhatFor}</th>
            <th scope="col">{w.privacyYourAnswer}</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {consents.map((c) => (
            <tr key={c.purpose}>
              <td>{purpose(w, c.purpose)}</td>
              <td>
                {c.given ? w.privacyGiven : c.at ? w.privacyWithdrawn : w.privacyNotAsked}
                {c.at ? (
                  <span className="small muted">
                    {" "}
                    · {w.privacyConsentDetail(day(c.at, language), c.noticeVersion ?? "", c.channel ?? "")}
                  </span>
                ) : null}
                {c.stale ? <span className="tag tag-plain"> {w.privacyNoticeChanged}</span> : null}
              </td>
              <td>
                {c.purpose === "account" && c.given ? (
                  // It cannot be withdrawn on its own: the button says why and what to do instead.
                  <button
                    type="button"
                    className="btn btn-small"
                    disabled={busy}
                    onClick={() => onChange(c.purpose, false)}
                  >
                    {w.privacyWithdraw}
                  </button>
                ) : (
                  <>
                    {/* A consent given under an older notice can be given again or withdrawn. */}
                    {!c.given || c.stale ? (
                      <button
                        type="button"
                        className="btn btn-small"
                        disabled={busy}
                        onClick={() => onChange(c.purpose, true)}
                      >
                        {w.privacyGiveConsent}
                      </button>
                    ) : null}
                    {c.given ? (
                      <button
                        type="button"
                        className="btn btn-small"
                        disabled={busy}
                        onClick={() => onChange(c.purpose, false)}
                      >
                        {w.privacyWithdraw}
                      </button>
                    ) : null}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** What withdrawing each consent stops, as the person reads it before confirming (#484). */
export const withdrawalStops = (w: Words, value: Exclude<ConsentPurpose, "account">): string =>
  ({
    sensitive_data: w.privacyStopsSensitiveData,
    quality_monitoring: w.privacyStopsQualityMonitoring,
    product_news: w.privacyStopsProductNews,
  })[value];

/**
 * Before a withdrawal: what it stops, in plain words, then confirm or keep it. Consent to run the account cannot be
 * withdrawn on its own, so for it the panel says why and offers deleting the account instead, or for an Owner who is
 * the Account Holder, offboarding the business.
 */
function ConfirmWithdrawal({
  purpose: which,
  owner,
  busy,
  onConfirm,
  onCancel,
}: {
  purpose: ConsentPurpose;
  owner: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const w = useWords();
  return (
    <section className="card" aria-label={w.privacyWhatItStops}>
      <h3>{purpose(w, which)}</h3>
      {which === "account" ? (
        <>
          <p>{w.privacyStopsAccount}</p>
          {owner ? <p>{w.privacyStopsAccountHolder}</p> : null}
          <button type="button" className="btn" onClick={onCancel}>
            {w.privacyKeepConsent}
          </button>
        </>
      ) : (
        <>
          <p>
            <strong>{w.privacyWhatItStops}:</strong> {withdrawalStops(w, which)}
          </p>
          <button type="button" className="btn btn-danger" disabled={busy} onClick={onConfirm}>
            {w.privacyConfirmWithdraw}
          </button>{" "}
          <button type="button" className="btn" disabled={busy} onClick={onCancel}>
            {w.privacyKeepConsent}
          </button>
        </>
      )}
    </section>
  );
}

function RequestForm({ api, onDone }: { api: Api; onDone: (request: RightsRequest) => void }) {
  const w = useWords();
  const { language } = useLanguage();
  const [right, setRight] = useState<Right>("access");
  const [about, setAbout] = useState<"account" | "business">("account");
  const [kind, setKind] = useState<"email" | "mobile">("email");
  const [value, setValue] = useState("");
  const [detail, setDetail] = useState("");
  const [receipt, setReceipt] = useState<RequestReceipt | undefined>(undefined);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      const input: NewRightsRequest = {
        identifier: { kind, value: value.trim() },
        right,
        about,
        detail: detail.trim(),
      };
      setReceipt(await api.privacy.request(input));
    } catch (error) {
      setProblem(requestProblemOf(error, w, language));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    if (!receipt) return;
    setBusy(true);
    setProblem(undefined);
    try {
      onDone(await api.privacy.verify(receipt.reference, code.trim()));
      setReceipt(undefined);
      setCode("");
      setDetail("");
    } catch (error) {
      setProblem(error instanceof ApiError && error.status < 500 ? w.privacyCodeFailed : problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  if (receipt) {
    return (
      <form className="card" aria-label={w.privacyConfirmRequest} onSubmit={(e) => void verify(e)}>
        <p role="status">{w.privacyRequestReceived(receipt.reference, day(receipt.dueBy, language), value.trim())}</p>
        <label className="field" htmlFor="request-code">
          {w.privacyCode}
        </label>
        <input
          id="request-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <button type="submit" className="btn btn-go" disabled={busy || !/^\d{6}$/.test(code.trim())}>
          {w.privacyConfirm}
        </button>
        {problem ? (
          <p className="alert" role="alert">
            {problem}
          </p>
        ) : null}
      </form>
    );
  }

  return (
    <form className="card" aria-label={w.privacyMakeRequest} onSubmit={(e) => void submit(e)} noValidate>
      <label className="field" htmlFor="request-right">
        {w.privacyWhatYouWant}
      </label>
      <select id="request-right" value={right} onChange={(e) => setRight(e.target.value as Right)}>
        {RIGHTS.map((r) => (
          <option key={r} value={r}>
            {rightOf(w, r)}
          </option>
        ))}
      </select>
      <fieldset>
        <legend>{w.privacyWhatAbout}</legend>
        <label>
          <input type="radio" name="about" checked={about === "account"} onChange={() => setAbout("account")} />{" "}
          {w.privacyAboutAccount}
        </label>
        <label>
          <input type="radio" name="about" checked={about === "business"} onChange={() => setAbout("business")} />{" "}
          {w.privacyAboutBusiness}
        </label>
      </fieldset>
      <label className="field" htmlFor="request-kind">
        {w.privacyYourRegistered}
      </label>
      <select id="request-kind" value={kind} onChange={(e) => setKind(e.target.value as "email" | "mobile")}>
        <option value="email">{w.privacyEmail}</option>
        <option value="mobile">{w.privacyMobile}</option>
      </select>
      <input
        aria-label={kind === "email" ? w.privacyRegisteredEmail : w.privacyRegisteredMobile}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <label className="field" htmlFor="request-detail">
        {w.privacyTellMore}
      </label>
      <textarea id="request-detail" maxLength={2000} value={detail} onChange={(e) => setDetail(e.target.value)} />
      <button type="submit" className="btn btn-go" disabled={busy || value.trim() === "" || detail.trim() === ""}>
        {w.privacySendRequest}
      </button>
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </form>
  );
}

function Requests({ requests }: { requests: readonly RightsRequest[] }) {
  const w = useWords();
  const { language } = useLanguage();
  if (requests.length === 0) return <p className="muted">{w.privacyNoRequests}</p>;
  return (
    <ul className="plain">
      {requests.map((r) => (
        <li key={r.reference}>
          <strong>{r.reference}</strong> · {rightOf(w, r.right)} · {status(w, r.status)}
          {r.completedAt
            ? ` ${w.privacyDoneOn(day(r.completedAt, language))}`
            : ` · ${w.privacyDue(day(r.dueBy, language))}`}
          {r.outcome ? <p className="small">{r.outcome}</p> : null}
        </li>
      ))}
    </ul>
  );
}

function BusinessRequests({ api }: { api: Api }) {
  const w = useWords();
  const { language } = useLanguage();
  const [open, setOpen] = useState<{ open: readonly RightsRequest[]; overdue: number } | undefined>(undefined);
  const [outcomes, setOutcomes] = useState<Readonly<Record<string, string>>>({});
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.privacy.tenantRequests().then(setOpen, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api, w]);
  useEffect(load, [load]);

  const complete = async (reference: string, refused: boolean) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await api.privacy.completeTenantRequest(reference, (outcomes[reference] ?? "").trim(), refused);
      load();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 className="sectionh">{w.privacyBusinessRequestsHeading}</h2>
      <p className="muted">{w.privacyBusinessRequestsIntro}</p>
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {open === undefined ? null : open.open.length === 0 ? (
        <p className="muted">{w.privacyNoneWaiting}</p>
      ) : (
        <>
          {open.overdue > 0 ? (
            <p className="alert" role="alert">
              {w.privacyOverdue(open.overdue)}
            </p>
          ) : null}
          <ul className="plain">
            {open.open.map((r) => (
              <li key={r.reference} className="card">
                <strong>{r.reference}</strong> · {rightOf(w, r.right)} · {w.privacyDue(day(r.dueBy, language))}
                <p>{r.detail}</p>
                <label className="field" htmlFor={`outcome-${r.reference}`}>
                  {w.privacyWhatYouDid}
                </label>
                <textarea
                  id={`outcome-${r.reference}`}
                  value={outcomes[r.reference] ?? ""}
                  onChange={(e) => setOutcomes({ ...outcomes, [r.reference]: e.target.value })}
                />
                <button
                  type="button"
                  className="btn btn-go"
                  disabled={busy || (outcomes[r.reference] ?? "").trim() === ""}
                  onClick={() => void complete(r.reference, false)}
                >
                  {w.privacyMarkDone}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy || (outcomes[r.reference] ?? "").trim() === ""}
                  onClick={() => void complete(r.reference, true)}
                >
                  {w.privacyRefuse}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function ReviewerAccess({ api }: { api: Api }) {
  const w = useWords();
  const { language } = useLanguage();
  const [entries, setEntries] = useState<readonly ReviewerAccessEntry[] | undefined>(undefined);
  useEffect(() => {
    api.privacy.reviewerAccess().then(setEntries, () => setEntries([]));
  }, [api]);
  return (
    <>
      <h2 className="sectionh">{w.privacyReviewerHeading}</h2>
      <p className="muted">{w.privacyReviewerIntro}</p>
      {entries === undefined ? null : entries.length === 0 ? (
        <p className="muted">{w.privacyNobodyLooked}</p>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{w.privacyWhen}</th>
                <th scope="col">{w.privacyWho}</th>
                <th scope="col">{w.privacyWhat}</th>
                <th scope="col">{w.privacyMasked}</th>
                <th scope="col">{w.privacyWhy}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.sequence}>
                  <td>{day(e.at, language)}</td>
                  <td>{e.staff}</td>
                  <td>
                    {e.subject}
                    {e.evalCase ? (
                      <span className="small muted"> · {w.privacyTestCase(e.evalCase, e.sharedCase)}</span>
                    ) : null}
                  </td>
                  <td>{e.unmasked ? <span className="tag tag-plain">{w.privacyUnmasked}</span> : w.privacyMasked}</td>
                  <td>{e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function OffboardingPanel({ api, run }: { api: Api; run: StepUpRun }) {
  const w = useWords();
  const { language } = useLanguage();
  const [status, setStatus] = useState<Offboarding | undefined>(undefined);
  const [refused, setRefused] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.privacy.offboarding().then(setStatus, (error: unknown) => setRefused(problemOf(error, w)));
  }, [api, w]);

  const step = async (to: () => Promise<Offboarding>, what?: string) => {
    setBusy(true);
    setProblem(undefined);
    try {
      setStatus(await run(to, what));
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 className="sectionh">{w.privacyLeaveHeading}</h2>
      {refused ? (
        <p className="muted">{w.privacyOnlyHolder}</p>
      ) : status === undefined ? null : status.stage === "shredded" ? (
        <p role="status">{w.privacyShredded}</p>
      ) : status.stage === "none" ? (
        <>
          <p className="muted">{w.privacyOffboardIntro}</p>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy}
            onClick={() => void step(() => api.privacy.startOffboarding(), w.privacyStartOffboardingWhat)}
          >
            {w.privacyStartOffboarding}
          </button>
        </>
      ) : (
        <>
          <p>{w.privacyDownloadBy(status.exportUntil ? day(status.exportUntil, language) : w.privacyEndOf30Days)}</p>
          <fieldset>
            <legend>{w.privacyConfirmEach}</legend>
            {(Object.keys(status.confirmations) as OffboardingConfirmation[]).map((key) => (
              <label key={key}>
                <input
                  type="checkbox"
                  checked={status.confirmed.includes(key)}
                  disabled={busy || status.confirmed.includes(key)}
                  onChange={() => void step(() => api.privacy.confirmOffboarding(key))}
                />{" "}
                {status.confirmations[key]}
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>{w.privacyArchive}</legend>
            {(["keep", "none"] as const).map((choice) => (
              <label key={choice}>
                <input
                  type="radio"
                  name="archive"
                  checked={status.archive === choice}
                  disabled={busy}
                  onChange={() => void step(() => api.privacy.chooseArchive(choice))}
                />{" "}
                {status.archiveChoices[choice]}
              </label>
            ))}
          </fieldset>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy || status.missing.length > 0}
            onClick={() => void step(() => api.privacy.shred(), w.privacyShredWhat)}
          >
            {w.privacyShredNow}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => void step(() => api.privacy.cancelOffboarding())}
          >
            {w.privacyStay}
          </button>
        </>
      )}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </>
  );
}

export function Privacy({ api, owner }: { api: Api; owner: boolean }) {
  const w = useWords();
  const { language } = useLanguage();
  const [overview, setOverview] = useState<PrivacyOverview | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  /** The consent the person asked to withdraw, waiting for them to read what it stops and confirm. */
  const [confirming, setConfirming] = useState<ConsentPurpose | undefined>(undefined);
  const stepUp = useStepUp(api.stepUp);

  const load = useCallback(() => {
    api.privacy.overview().then(setOverview, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api, w]);
  useEffect(load, [load]);

  const changeConsent = async (purpose: ConsentPurpose, give: boolean) => {
    if (!overview) return;
    setBusy(true);
    setProblem(undefined);
    setNotice(undefined);
    try {
      const consents = give ? await api.privacy.giveConsent(purpose) : await api.privacy.withdrawConsent(purpose);
      setOverview({ ...overview, consents });
      setConfirming(undefined);
      setNotice(give ? w.privacyConsentRecorded : w.privacyConsentWithdrawn);
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  if (!overview) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.privacyLoading}</p>;
  return (
    <>
      <p className="muted">
        {w.privacyIntro} <a href="/sub-processors">{w.privacySubProcessorLink}</a>
        {w.privacyIntroEnd}
      </p>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <h2 className="sectionh">{w.privacyConsentHeading}</h2>
      <Consents
        consents={overview.consents}
        noticeVersion={overview.noticeVersion}
        busy={busy}
        onChange={(p, g) => {
          setNotice(undefined);
          if (g) void changeConsent(p, g);
          else setConfirming(p);
        }}
      />
      {confirming ? (
        <ConfirmWithdrawal
          purpose={confirming}
          owner={owner}
          busy={busy}
          onConfirm={() => void changeConsent(confirming, false)}
          onCancel={() => setConfirming(undefined)}
        />
      ) : null}
      <h2 className="sectionh">{w.privacyRightsHeading}</h2>
      <p className="muted">{w.privacyRightsIntro}</p>
      <RequestForm
        api={api}
        onDone={(request) => {
          setNotice(requestNotice(request, language));
          load();
        }}
      />
      <h2 className="sectionh">{w.privacyRequestsHeading}</h2>
      <Requests requests={overview.requests} />
      {owner ? (
        <>
          <BusinessRequests api={api} />
          <ReviewerAccess api={api} />
          <h2 className="sectionh">{w.privacyExportHeading}</h2>
          <p className="muted">{w.privacyExportIntro}</p>
          <ExportButton api={api} run={stepUp.run} />
          <OffboardingPanel api={api} run={stepUp.run} />
        </>
      ) : null}
      {stepUp.dialog}
    </>
  );
}
